import { doc, setDoc, updateDoc, getDoc, getDocs, collection, query, orderBy, limit, runTransaction, serverTimestamp } from 'firebase/firestore'
import { db } from '@/core/firebase/client'
import { PlaceRequestSchema, placeRequestKey, type PlaceRequestDoc } from '@/core/models/place-request'
import { cityKeyForSpain } from '@/core/models/padel-place'

// `place_requests` — see the model for WHY this is one inbox with two origins,
// and why the player side has no button.

const COL = 'place_requests'

/**
 * Record that somebody could not find a club. Idempotent per typed name: the
 * SAME place typed again increments `matchCount` on one document instead of
 * adding a near-duplicate to the inbox — which is what makes the count mean
 * "demand" rather than "how many rows this club has".
 *
 * Transactional because two players typing the same missing club at once must
 * end at 2, not 1. That is the entire value of the number.
 *
 * Never throws at the call site's expense: this rides along with a match
 * creation, and a failed suggestion must not fail the thing the player was
 * actually doing. Logged and swallowed.
 */
export async function recordPlaceRequest(args: {
  typed: string
  /**
   * The city of the PERSON who typed this, not of the club — we do not know
   * the club's city, that is the whole reason it is not in the directory yet.
   * Named for what it is: it was called `city`, the verify form pre-filled the
   * club's city from it, and a player in Granada typing a club in Órgiva
   * therefore proposed "Granada" as fact. A proxy presented as a fact is worse
   * than a blank, because a blank gets checked.
   *
   * Useful as a HINT (most people type clubs near themselves), never as an
   * answer.
   */
  requesterCity: string | null
  uid: string
  origin?: 'player' | 'club_owner'
  clubManagerId?: string | null
}): Promise<void> {
  const key = placeRequestKey(args.typed)
  if (!key) return // punctuation-only text is not a club
  try {
    await runTransaction(db, async (tx) => {
      const ref = doc(db, COL, key)
      const snap = await tx.get(ref)
      const base = {
        origin: args.origin ?? 'player',
        typed: args.typed.trim(),
        typedKey: key,
        requesterCity: args.requesterCity,
        clubManagerId: args.clubManagerId ?? null,
        lastRequestedBy: args.uid,
        status: 'pending' as const,
        resolvedPlaceId: null,
        updatedAt: serverTimestamp(),
      }
      if (!snap.exists()) {
        tx.set(ref, { ...base, matchCount: 1, createdAt: serverTimestamp() })
        return
      }
      // An already-RESOLVED request that gets typed again is a real signal —
      // the club still is not findable — so it reopens rather than silently
      // incrementing a closed row nobody looks at.
      const prev = snap.data()
      tx.set(ref, { ...base, matchCount: (Number(prev['matchCount']) || 0) + 1 }, { merge: true })
    })
  } catch (e) {
    console.warn('[places] could not record place request', e)
  }
}

/** The operator inbox, busiest first — demand order, not arrival order. */
export async function listPlaceRequests(max = 100): Promise<PlaceRequestDoc[]> {
  const snap = await getDocs(query(collection(db, COL), orderBy('matchCount', 'desc'), limit(max)))
  return snap.docs
    .map((d) => PlaceRequestSchema.safeParse({ id: d.id, ...d.data() }))
    .filter((r): r is { success: true; data: PlaceRequestDoc } => r.success)
    .map((r) => r.data)
}

// ── RESOLVING — the operator's half ──────────────────────────────────────────
// Every resolution KEEPS the request document. A rejected request that vanished
// would be a club we reject again next month, and a resolved one is how we know
// the typed name maps to a row when somebody types it again.

/** Reject: this is not a club, or not one we want listed. */
export async function rejectPlaceRequest(id: string): Promise<void> {
  await updateDoc(doc(db, COL, id), { status: 'rejected', updatedAt: serverTimestamp() })
}

/**
 * Merge: the club IS in the directory, under a name nobody typed.
 *
 * When the request came from a CLUB OWNER, merging is also THE LINK: it hands
 * authority for that row's operational data to clubmanager
 * (spec/usersmatch/01 § Who owns a club row). The row keeps its id forever —
 * matches point at `padelPlaceId`, and an id that moves is a broken match.
 *
 * `clubManagerId` is written only when the request carries one, so merging a
 * player's typed venue stays a plain merge and cannot accidentally hand a row
 * to a club that never asked for it.
 */
export async function mergePlaceRequest(
  id: string,
  placeId: string,
  clubManagerId?: string | null,
): Promise<void> {
  if (clubManagerId) {
    // ONE ROW, ONE OWNER. Refused rather than mediated: a row already claimed
    // by another club must never be handed over by a merge, and the operator
    // needs to see that rather than discover it later in the directory.
    const target = await getDoc(doc(db, 'places_courts', placeId))
    const existing = target.data()?.['clubManagerId'] as string | undefined
    if (existing && existing !== clubManagerId) {
      throw new Error('already-claimed')
    }
    await updateDoc(doc(db, 'places_courts', placeId), {
      clubManagerId,
      source: 'clubmanager',
      updatedAt: serverTimestamp(),
    })
  }
  await updateDoc(doc(db, COL, id), {
    status: 'merged',
    resolvedPlaceId: placeId,
    updatedAt: serverTimestamp(),
  })
}

/**
 * Create a directory row from a request, then resolve it.
 *
 * The row gets a usersmatch-owned id — `source: 'manual'` — because usersmatch
 * owns this directory (spec/usersmatch/01). Even if this club later joins
 * clubmanager, the id stays: matches point at `padelPlaceId`, and an id that
 * moves is a broken match.
 *
 * Row first, request second, deliberately. If the second write fails the
 * operator sees the request still pending and retries — a duplicate row is
 * visible and mergeable. The other order would resolve a request pointing at a
 * club that does not exist.
 */
export async function createPlaceFromRequest(
  id: string,
  place: { name: string; city: string; address: string; courts: number },
): Promise<string> {
  const placeId = `manual-${placeRequestKey(place.name) || id}`
  await setDoc(
    doc(db, 'places_courts', placeId),
    {
      id: placeId,
      source: 'manual',
      clubManagerId: null,
      name: place.name,
      address: place.address,
      city: place.city,
      cityKey: cityKeyForSpain(place.city),
      numberOfCourts: place.courts,
      isActive: true,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    },
    { merge: true },
  )
  await mergePlaceRequest(id, placeId)
  await updateDoc(doc(db, COL, id), { status: 'created' })
  return placeId
}

/** Inline directory edits — usersmatch owns these rows, so the operator may. */
export async function updatePlace(
  placeId: string,
  patch: { name?: string; address?: string; numberOfCourts?: number },
): Promise<void> {
  await updateDoc(doc(db, 'places_courts', placeId), { ...patch, updatedAt: serverTimestamp() })
}
